import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarIcon } from "../../components/icons";
import { fetchPlaces } from "../../lib/dashboard-data";
import { FIN_PRESETS, finRangeDays } from "../../lib/finance-report";
import { MAX_DAYS } from "../../lib/stats-range";
import type { FinanceFiltersState } from "./filters";

/**
 * ตัวกรองร่วมของจอ ລາຍງານການເງິນ กับ ລາຍຈ່າຍ: ช่วงเวลา (เดือน/ปี ตามเวลาลาว) + พื้นที่ (ແຂວງ → ເມືອງ → ຕາແສງ)
 *
 * ทั้งสองอย่างอยู่ใน URL (แชร์ลิงก์แล้วเห็นชุดเดียวกัน) · ตัวเลือกพื้นที่มาจาก /stats/places เท่านั้น
 * (เฉพาะที่อยู่ใน scope ของบัญชี) — server ตรวจ scope ซ้ำทุกครั้ง นอกเขต = 403
 */

export function FinanceFilters({ f }: { f: FinanceFiltersState }) {
  const { range, place, nowMs, go } = f;
  const places = useQuery({ queryKey: ["stats", "places"], queryFn: fetchPlaces, staleTime: 5 * 60_000 });
  const today = finRangeDays({ kind: "month" }, nowMs).to;
  const [customOpen, setCustomOpen] = useState(range.kind === "custom");
  const [from, setFrom] = useState(f.days.from);
  const [to, setTo] = useState(f.days.to);
  const span = from && to ? Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1 : 0;
  const invalid =
    !from || !to || from > to
      ? "ວັນທີເລີ່ມຕ້ອງບໍ່ຫຼັງວັນທີສຸດທ້າຍ"
      : to > today
        ? "ເລືອກວັນທີໃນອະນາຄົດບໍ່ໄດ້"
        : span > MAX_DAYS
          ? `ເລືອກໄດ້ບໍ່ເກີນ ${String(MAX_DAYS)} ວັນ`
          : null;

  // ตัวเลือกพื้นที่: แขวง = แม่ของเมืองใน scope · เมือง/ตาแสง แคบลงตามระดับที่เลือกไว้
  const districts = places.data?.districts ?? [];
  const tasaengs = places.data?.tasaengs ?? [];
  const provinces = [...new Map(districts.map((d) => [d.parent_id, d.parent_name_lo])).entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "lo"));
  const districtOpts = districts
    .filter((d) => !place.province || d.parent_id === place.province)
    .sort((a, b) => a.name_lo.localeCompare(b.name_lo, "lo"));
  const districtIds = new Set(districtOpts.map((d) => d.id));
  const tasaengOpts = tasaengs
    .filter((t) => (place.district ? t.parent_id === place.district : districtIds.has(t.parent_id)))
    .sort((a, b) => a.name_lo.localeCompare(b.name_lo, "lo"));
  const anyPlace = Boolean(place.province || place.district || place.tasaeng);

  return (
    <div className="fin-filters">
      <div className="range-bar">
        <div className="seg" role="group" aria-label="ຊ່ວງເວລາ">
          {FIN_PRESETS.map((p) => (
            <button
              key={p.kind}
              type="button"
              className={!customOpen && range.kind === p.kind ? "seg__btn is-active" : "seg__btn"}
              onClick={() => {
                setCustomOpen(false);
                go({ range: { kind: p.kind } });
              }}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={customOpen ? "seg__btn is-active" : "seg__btn"}
            onClick={() => setCustomOpen(true)}
          >
            <CalendarIcon size={14} /> ກຳນົດເອງ
          </button>
        </div>

        {customOpen ? (
          <form
            className="range-bar__custom"
            onSubmit={(e) => {
              e.preventDefault();
              if (!invalid) go({ range: { kind: "custom", from, to } });
            }}
          >
            <label className="range-bar__date">
              <span>ຈາກ</span>
              <input
                type="date"
                className="input-field num"
                value={from}
                max={today}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="range-bar__date">
              <span>ຫາ</span>
              <input type="date" className="input-field num" value={to} max={today} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button type="submit" className="btn-secondary" disabled={invalid !== null}>
              ນຳໃຊ້
            </button>
            {invalid ? <span className="range-bar__error">{invalid}</span> : null}
          </form>
        ) : null}

        <span className="range-bar__text">
          <CalendarIcon size={14} />
          <span className="num">{f.periodText}</span>
        </span>
      </div>

      {provinces.length > 1 || districtOpts.length > 1 || tasaengOpts.length > 1 || anyPlace ? (
        <div className="place-filter fin-filters__place">
          {provinces.length > 1 ? (
            <select
              className="filter-select"
              aria-label="ແຂວງ"
              value={place.province ?? ""}
              onChange={(e) => go({ place: { province: e.target.value || undefined } })}
            >
              <option value="">ທຸກແຂວງ</option>
              {provinces.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : null}
          {districtOpts.length > 1 ? (
            <select
              className="filter-select"
              aria-label="ເມືອງ"
              value={place.district ?? ""}
              onChange={(e) => go({ place: { province: place.province, district: e.target.value || undefined } })}
            >
              <option value="">ທຸກເມືອງ</option>
              {districtOpts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name_lo}
                </option>
              ))}
            </select>
          ) : null}
          {tasaengOpts.length > 1 ? (
            <select
              className="filter-select"
              aria-label="ຕາແສງ"
              value={place.tasaeng ?? ""}
              onChange={(e) =>
                go({ place: { province: place.province, district: place.district, tasaeng: e.target.value || undefined } })
              }
            >
              <option value="">ທຸກຕາແສງ</option>
              {tasaengOpts.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name_lo}
                </option>
              ))}
            </select>
          ) : null}
          {anyPlace ? (
            <button type="button" className="btn-secondary btn-sm place-filter__clear" onClick={() => go({ place: {} })}>
              ລ້າງຕົວກອງພື້ນທີ່
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
